import { ReactNode, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Paper,
  Grid2,
  Chip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  formatDateValue,
  formatSensitiveValue,
  handleCopyToClipboard,
  handleDownload
} from '@/src/utils/CredentialUtils';
import ConfirmDialog from '@/src/components/common/ConfirmDialog';
import { CredentialAttributeDef } from '@/src/types/common.interface';

interface CredentialDialogueProps {
  open: boolean;
  credentialData: any;
  attributeDefinitions: CredentialAttributeDef[];
  copyDownloadFields: string[];
  onClose: () => void;
  title?: string;
}

const CredentialDialogue = ({
  open,
  credentialData,
  onClose,
  title = 'Credential Information',
  attributeDefinitions,
  copyDownloadFields
}: CredentialDialogueProps) => {
  const [copied, setCopied] = useState(false);
  const [visibilityStates, setVisibilityStates] = useState<Record<string, boolean>>({});

  const handleCopyToClipboardAction = async () => {
    const success = await handleCopyToClipboard(credentialData, copyDownloadFields);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadAction = () => {
    const clientId = credentialData.client_id;
    const fileName = `${clientId}.json`;
    handleDownload(credentialData, copyDownloadFields, fileName);
  };

  const toggleVisibility = (key: string) => {
    setVisibilityStates((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const renderAttributeValue = (attribute: CredentialAttributeDef, value: any) => {
    const isVisible = visibilityStates[attribute.key] || false;

    switch (attribute.valueType) {
      case 'monospace':
        const displayValue = attribute.isSensitive
          ? formatSensitiveValue(value || '', isVisible)
          : value || '-';
        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f0f0f0',
              padding: '4px 8px',
              borderRadius: 1,
              flex: 1
            }}
          >
            {displayValue}
          </Typography>
        );

      case 'date':
        return (
          <Typography variant="body2">
            {value ? formatDateValue(value, attribute.dateFormat) : '-'}
          </Typography>
        );

      case 'chips':
        return (
          <Box>
            {value && Array.isArray(value) && value.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {value.map((item: string, index: number) => (
                  <Chip
                    key={index}
                    label={item}
                    size="small"
                    color={attribute.chipColor || 'primary'}
                    variant={attribute.chipVariant || 'outlined'}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Box>
        );

      case 'hidden':
        return null;

      case 'text':
      default:
        return <Typography variant="body2">{value || '-'}</Typography>;
    }
  };

  const dialogueMessage = (): ReactNode => {
    return (
      <Box sx={{ minWidth: 500, maxWidth: 600 }}>
        {/* Security Warning */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Security Notice</AlertTitle>
          Please download or copy the credential keys and data. This information will not be
          available in the future for security reasons.
        </Alert>

        {/* Credential Data Display */}
        <Typography variant="h6" gutterBottom color="primary.dark">
          {title}
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 2,
            backgroundColor: '#fafafa'
          }}
        >
          <Grid2 container spacing={2}>
            {attributeDefinitions.map((attribute) => {
              // Skip hidden attributes
              if (attribute.valueType === 'hidden') {
                return null;
              }

              const value = credentialData[attribute.key];
              const isVisible = visibilityStates[attribute.key] || false;

              return (
                <Grid2 key={attribute.key} size={{ xs: 12 }}>
                  <Grid2
                    container
                    spacing={2}
                    alignItems={attribute.valueType === 'chips' ? 'flex-start' : 'center'}
                  >
                    {/* Label Column */}
                    <Grid2 size={{ xs: 4 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{
                          mt: attribute.valueType === 'chips' ? 0.5 : 0
                        }}
                      >
                        {attribute.label}:
                      </Typography>
                    </Grid2>

                    {/* Value Column */}
                    <Grid2 size={{ xs: 8 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {renderAttributeValue(attribute, value)}

                        {/* Visibility Toggle for Sensitive Fields */}
                        {attribute.showVisibilityToggle && attribute.isSensitive && (
                          <Tooltip
                            title={
                              isVisible ? `Hide ${attribute.label}` : `Show ${attribute.label}`
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => toggleVisibility(attribute.key)}
                              color="primary"
                            >
                              {isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Grid2>
                  </Grid2>
                </Grid2>
              );
            })}
          </Grid2>
        </Paper>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={handleCopyToClipboardAction}
            startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
            color={copied ? 'success' : 'primary'}
          >
            {copied ? 'Copied!' : 'Copy JSON'}
          </Button>

          <Button
            variant="contained"
            onClick={handleDownloadAction}
            startIcon={<DownloadIcon />}
            color="primary"
          >
            Download JSON
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <ConfirmDialog
      content={dialogueMessage()}
      handleCloseAction={onClose}
      handleSaveAction={onClose}
      open={open}
      title={title}
      closeActionName="Close"
      isHideSave
    />
  );
};

export default CredentialDialogue;
